import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { Court, RecurringEvent } from '../api_types';
import { recurringEventApi } from '../api';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import ConflictStatus from '../shared/components/ConflictStatus';

interface RecurringEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  facilityId: string;
  businessId: string;
  courts: Court[];
  mode: 'create' | 'edit';
  event?: RecurringEvent;
  timeOptions: string[];
}

interface RecurringEventData {
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endTime: string;
  courtIds: string[];
  capacity?: number;
  registrationRequired: boolean;
  registrationDeadline?: string;
  price?: number;
  status: string;
  id?: string;
  recurrenceRule: {
    frequency: 'daily' | 'weekly' | 'monthly' | 'custom';
    interval?: number;
    daysOfWeek?: number[];
    dayOfMonth?: number;
    endDate?: string;
    occurrences?: number;
  };
}

const RecurringEventModal: React.FC<RecurringEventModalProps> = ({
  open,
  onClose,
  onSave,
  facilityId,
  businessId,
  courts,
  mode,
  event,
  timeOptions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [eventData, setEventData] = useState<RecurringEventData>({
    title: event?.title || '',
    description: event?.description || '',
    startDate: event?.startDate || format(new Date(), 'yyyy-MM-dd'),
    startTime: event?.startTime || '09:00',
    endTime: event?.endTime || '10:00',
    courtIds: event?.courtIds || [],
    capacity: event?.capacity || 0,
    registrationRequired: event?.registrationRequired || false,
    price: event?.price || 0,
    status: event?.status || 'active',
    id: event?.id,
    recurrenceRule: {
      frequency: event?.recurrenceRule.frequency || 'weekly',
      interval: event?.recurrenceRule.interval || 1,
      daysOfWeek: event?.recurrenceRule.daysOfWeek || [],
      endDate: event?.recurrenceRule.endDate || format(new Date(new Date().setMonth(new Date().getMonth() + 3)), 'yyyy-MM-dd'),
    },
  });
  const [timeError, setTimeError] = useState<string | null>(null);
  const [hasConflict, setHasConflict] = useState(false);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  // Conflict check effect
  useEffect(() => {
    async function checkConflicts() {
      setTimeError(null);
      setHasConflict(false);
      if (!eventData.startTime || !eventData.endTime || eventData.courtIds.length === 0) return;

      // Basic validation
      if (!eventData.startDate || !eventData.recurrenceRule.endDate) {
        return;
      }

      setIsCheckingConflicts(true);
      try {
        const startDate = parseISO(eventData.startDate);
        const endDate = parseISO(eventData.recurrenceRule.endDate);
        
        // Generate all dates for the recurring event
        const allDates: Date[] = [];
        const interval = eventData.recurrenceRule.interval || 1;
        
        if (eventData.recurrenceRule.frequency === 'daily') {
          // Daily events: Jump by interval days
          let currentDate = new Date(startDate);
          while (currentDate <= endDate) {
            allDates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + interval);
          }
        } else if (eventData.recurrenceRule.frequency === 'weekly' && eventData.recurrenceRule.daysOfWeek?.length) {
          // Weekly events: Calculate exact dates for selected days and jump by weeks
          let weekStart = new Date(startDate);
          weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Go to start of week (Sunday)
          
          while (weekStart <= endDate) {
            for (const dayOfWeek of eventData.recurrenceRule.daysOfWeek) {
              const eventDate = new Date(weekStart);
              eventDate.setDate(weekStart.getDate() + dayOfWeek);
              
              if (eventDate >= startDate && eventDate <= endDate) {
                allDates.push(new Date(eventDate));
              }
            }
            // Jump by interval weeks
            weekStart.setDate(weekStart.getDate() + (7 * interval));
          }
        } else if (eventData.recurrenceRule.frequency === 'monthly') {
          // Monthly events: Jump month by month
          let currentDate = new Date(startDate);
          while (currentDate <= endDate) {
            allDates.push(new Date(currentDate));
            currentDate.setMonth(currentDate.getMonth() + interval);
          }
        }

        // Use optimized batch check for all dates and courts
        const dateStrings = allDates.map(date => format(date, 'yyyy-MM-dd'));
        const result = await recurringEventApi.batchCheckRecurringConflicts(businessId, facilityId, {
          courtIds: eventData.courtIds,
          dates: dateStrings,
          startTime: eventData.startTime,
          endTime: eventData.endTime,
          excludeRecurringEventId: event?.id,
        });
        
        if (result.hasConflict) {
          setTimeError(result.conflictMessage || 'There is a scheduling conflict');
          setHasConflict(true);
        }
      } catch (err) {
        console.error('Error checking conflicts:', err);
        setTimeError('Error checking for conflicts');
        setHasConflict(true);
      } finally {
        setIsCheckingConflicts(false);
      }
    }
    
    // Debounce conflict checking to avoid excessive API calls
    const timeoutId = setTimeout(checkConflicts, 500);
    return () => clearTimeout(timeoutId);
  }, [eventData.startDate, eventData.endTime, eventData.startTime, eventData.courtIds, facilityId, event?.id, eventData.recurrenceRule]);

  const handleInputChange = (field: keyof RecurringEventData, value: any) => {
    setEventData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRecurrenceChange = (field: keyof RecurringEventData['recurrenceRule'], value: any) => {
    setEventData(prev => ({
      ...prev,
      recurrenceRule: {
        ...prev.recurrenceRule,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setTimeError(null);
    if (hasConflict) return;
    
    if (!timeOptions.includes(eventData.startTime)) {
      setTimeError("Selected start time is outside business hours");
      return;
    }

    if (!timeOptions.includes(eventData.endTime)) {
      setTimeError("Selected end time is outside business hours");
      return;
    }

    const [startHour, startMinute] = eventData.startTime.split(':').map(Number);
    const [endHour, endMinute] = eventData.endTime.split(':').map(Number);
    
    if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
      setTimeError("End time must be later than start time");
      return;
    }

    // If no conflicts, proceed
    if (mode === 'create') {
      await recurringEventApi.createRecurringEvent(businessId, facilityId, eventData);
    } else if (event?.id) {
      await recurringEventApi.updateRecurringEvent(businessId, facilityId, event.id, eventData);
    }
    onSave();
    onClose();
  };

  // Form validation - check if all required fields are filled
  const isFormValid = () => {
    const requiredFields = [
      eventData.title.trim(),
      eventData.startDate,
      eventData.startTime,
      eventData.endTime,
      eventData.courtIds.length > 0,
      eventData.recurrenceRule.frequency,
      eventData.recurrenceRule.endDate,
      eventData.recurrenceRule.interval && eventData.recurrenceRule.interval > 0,
    ];

    // Additional validation for weekly frequency - must have days selected
    if (eventData.recurrenceRule.frequency === 'weekly') {
      requiredFields.push(
        eventData.recurrenceRule.daysOfWeek && eventData.recurrenceRule.daysOfWeek.length > 0
      );
    }

    return requiredFields.every(field => Boolean(field));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 30px 60px -12px rgba(50,50,93,0.25),0 18px 36px -18px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          maxHeight: '90vh',
          m: isMobile ? 2 : 4,
        }
      }}
    >
      <Box sx={{ 
        p: { xs: 2, sm: 3, md: 4 },
        overflowY: 'auto' 
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 4 } }}>
          <Typography variant="h5" sx={{ 
            fontSize: { xs: '20px', md: '24px' },
            fontWeight: 600,
            color: '#1a1f36',
            letterSpacing: '-0.01em',
          }}>
            {mode === 'create' ? 'Create recurring event' : 'Edit recurring event'}
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              color: '#697386',
              '&:hover': {
                backgroundColor: '#f6f8fa',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Event title *"
                value={eventData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Description"
                value={eventData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                Start date *
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={eventData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                Start time *
              </Typography>
              <FormControl fullWidth error={!!timeError}>
                <Select
                  value={eventData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  {timeOptions.map((time) => (
                    <MenuItem key={time} value={time}>
                      {format(new Date(`2000-01-01T${time}`), 'h:mm a')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                End time *
              </Typography>
              <FormControl fullWidth error={!!timeError}>
                <Select
                  value={eventData.endTime}
                  onChange={(e) => handleInputChange('endTime', e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  {timeOptions.map((time) => (
                    <MenuItem key={time} value={time}>
                      {format(new Date(`2000-01-01T${time}`), 'h:mm a')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <ConflictStatus 
                hasConflict={hasConflict} 
                conflictMessage={timeError} 
                isChecking={isCheckingConflicts}
                showNoConflicts={Boolean(
                  eventData.startTime && 
                  eventData.endTime && 
                  eventData.courtIds.length > 0 && 
                  eventData.startDate && 
                  eventData.recurrenceRule.endDate
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                Courts *
              </Typography>
              <FormControl fullWidth>
                <Select
                  multiple
                  displayEmpty
                  value={eventData.courtIds}
                  onChange={(e) => handleInputChange('courtIds', e.target.value)}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography sx={{ color: '#697386' }}>Select courts</Typography>;
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((courtId) => {
                          const court = courts.find(c => c.id === courtId);
                          return (
                            <Typography key={courtId} variant="body2">
                              {court?.name}
                            </Typography>
                          );
                        })}
                      </Box>
                    );
                  }}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  {courts.map((court) => (
                    <MenuItem key={court.id} value={court.id}>
                      {court.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                Capacity
              </Typography>
              <TextField
                fullWidth
                type="number"
                placeholder="0"
                value={eventData.capacity}
                onChange={(e) => handleInputChange('capacity', parseInt(e.target.value))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                Price
              </Typography>
              <TextField
                fullWidth
                type="number"
                placeholder="$0"
                value={eventData.price}
                onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                InputProps={{
                  startAdornment: <Typography sx={{ color: '#697386', mr: 1 }}>$</Typography>,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={eventData.registrationRequired ?? false}
                    onChange={(e) => handleInputChange('registrationRequired', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase': {
                        '&.Mui-checked': {
                          color: '#625afa',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#625afa',
                            opacity: 0.5,
                          },
                        },
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 22 / 2,
                      },
                      '& .MuiSwitch-thumb': {
                        boxShadow: 'none',
                        width: 16,
                        height: 16,
                        margin: 2,
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: '#1a1f36', fontWeight: 500 }}>
                    Registration required
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#1a1f36',
                  mt: 2,
                  mb: 3,
                }}
              >
                Recurrence settings
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                Frequency *
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={eventData.recurrenceRule.frequency}
                  onChange={(e) => handleRecurrenceChange('frequency', e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                Interval *
              </Typography>
              <TextField
                fullWidth
                type="number"
                placeholder="1"
                value={eventData.recurrenceRule.interval}
                onChange={(e) => handleRecurrenceChange('interval', parseInt(e.target.value))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              />
            </Grid>

            {eventData.recurrenceRule.frequency === 'weekly' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  Days of week *
                </Typography>
                <FormControl fullWidth>
                  <Select
                    multiple
                    displayEmpty
                    value={eventData.recurrenceRule.daysOfWeek}
                    onChange={(e) => handleRecurrenceChange('daysOfWeek', e.target.value)}
                    renderValue={(selected) => {
                      if (!selected?.length) return <Typography sx={{ color: '#697386' }}>Select days</Typography>;
                      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map(day => (
                            <Chip
                              key={day}
                              label={days[day]}
                              size="small"
                              sx={{
                                backgroundColor: alpha('#625afa', 0.1),
                                color: '#625afa',
                                borderRadius: '6px',
                                height: '24px',
                                fontSize: '12px',
                              }}
                            />
                          ))}
                        </Box>
                      );
                    }}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#f6f8fa',
                      border: '1px solid #e3e8ee',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#c1c9d2',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        borderColor: '#625afa',
                        boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                  >
                    <MenuItem value={0}>Sunday</MenuItem>
                    <MenuItem value={1}>Monday</MenuItem>
                    <MenuItem value={2}>Tuesday</MenuItem>
                    <MenuItem value={3}>Wednesday</MenuItem>
                    <MenuItem value={4}>Thursday</MenuItem>
                    <MenuItem value={5}>Friday</MenuItem>
                    <MenuItem value={6}>Saturday</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                End date *
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={eventData.recurrenceRule.endDate}
                onChange={(e) => handleRecurrenceChange('endDate', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2, 
          pt: 3,
          mt: 2,
          borderTop: '1px solid #e3e8ee',
        }}>
          <Button 
            onClick={onClose}
            sx={{
              order: isMobile ? 2 : 1,
              color: '#1a1f36',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#f6f8fa',
              },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            fullWidth={isMobile}
            disabled={!isFormValid() || hasConflict || isCheckingConflicts}
            sx={{
              order: isMobile ? 1 : 2,
              backgroundColor: '#625afa',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
              padding: '10px 20px',
              '&:hover': {
                backgroundColor: '#4b44c9',
              },
            }}
          >
            {isCheckingConflicts ? 'Checking conflicts...' : mode === 'create' ? 'Create recurring event' : 'Save changes'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RecurringEventModal; 